import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import MetaOAuthBridgeIframe from './MetaOAuthBridgeIframe';

const ReAuthContext = createContext(null);

const serviceConfig = {
  whatsapp: {
    label: 'WhatsApp',
    nameKeys: ['display_name', 'phone_number'],
    detailKeys: ['phone_number', 'waba_id', 'profile_id'],
  },
  facebook: {
    label: 'Facebook',
    nameKeys: ['display_name'],
    detailKeys: ['category', 'profile_id'],
  },
  instagram: {
    label: 'Instagram',
    nameKeys: ['display_name'],
    detailKeys: ['category', 'business_account_id', 'profile_id'],
  },
};

const firstText = (account, keys = []) =>
  keys.map((key) => account?.[key]).find((value) => String(value || '').trim()) || '';

const normalizeInactiveProfile = (account) => {
  const service = account?.service_name || account?.service || '';
  const config = serviceConfig[service];
  if (!config) return null;

  return {
    ...account,
    service,
    serviceLabel: config.label,
    profileID: account?.profile_id || '',
    accountID: account?.id || '',
    displayName: firstText(account, config.nameKeys) || config.label,
    detail: firstText(account, config.detailKeys),
  };
};

export function ReAuthProvider({ children }) {
  const [inactiveProfiles, setInactiveProfiles] = useState([]);
  const [inactiveProfilesLoading, setInactiveProfilesLoading] = useState(false);

  const syncInactiveProfiles = useCallback((accounts = []) => {
    const nextProfiles = (accounts || [])
      .filter((account) => account?.is_enable !== false && account?.is_active === false)
      .map(normalizeInactiveProfile)
      .filter(Boolean);
    setInactiveProfiles(nextProfiles);
    return nextProfiles;
  }, []);

  const refreshInactiveProfiles = useCallback((silent = false) => {
    if (!CommonHelper.GetCurrentBrandId()) {
      setInactiveProfiles([]);
      return Promise.resolve([]);
    }
    if (!silent) setInactiveProfilesLoading(true);

    return new Promise((resolve) => {
      HttpServices.Get({
        url: userModuleURL.connectedProfiles,
        successCallback: (data) => {
          const nextProfiles = syncInactiveProfiles(data?.accounts || []);
          setInactiveProfilesLoading(false);
          resolve(nextProfiles);
        },
        failureCallback: () => {
          setInactiveProfiles([]);
          setInactiveProfilesLoading(false);
          resolve([]);
        },
      });
    });
  }, [syncInactiveProfiles]);

  const clearInactiveProfile = useCallback((service, profileID) => {
    setInactiveProfiles((current) =>
      current.filter((profile) => !(profile.service === service && String(profile.profileID) === String(profileID)))
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      inactiveProfiles,
      inactiveProfilesLoading,
      refreshInactiveProfiles,
      syncInactiveProfiles,
      clearInactiveProfile,
    }),
    [clearInactiveProfile, inactiveProfiles, inactiveProfilesLoading, refreshInactiveProfiles, syncInactiveProfiles]
  );

  return (
    <ReAuthContext.Provider value={contextValue}>
      {children}
      <MetaOAuthBridgeIframe />
    </ReAuthContext.Provider>
  );
}

export const useReAuth = () => useContext(ReAuthContext);
